import React, { useCallback, useEffect, useState } from 'react';
import { guideService } from '../services/api';
import RichNewsEditor from '../components/RichNewsEditor';
import type { GuideArticle, GuideCategory } from '../types';
import { slugify } from '../utils/guideLocale';
import '../styles/AdminGuides.css';

const emptyCategory: GuideCategory = {
    slug: '',
    titleEn: '',
    titlePtBr: '',
    sortOrder: 0,
    isVisible: true,
};

const emptyArticle: GuideArticle = {
    categoryId: 0,
    slug: '',
    titleEn: '',
    titlePtBr: '',
    summaryEn: '',
    summaryPtBr: '',
    contentEn: '',
    contentPtBr: '',
    sortOrder: 0,
    isPublished: true,
};

const AdminGuides: React.FC = () => {
    const [tab, setTab] = useState<'articles' | 'categories'>('articles');
    const [categories, setCategories] = useState<GuideCategory[]>([]);
    const [articles, setArticles] = useState<GuideArticle[]>([]);
    const [catForm, setCatForm] = useState<GuideCategory>(emptyCategory);
    const [artForm, setArtForm] = useState<GuideArticle>(emptyArticle);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [cats, arts] = await Promise.all([
                guideService.getAdminCategories(),
                guideService.getAdminArticles(),
            ]);
            setCategories(cats);
            setArticles(arts);
            setArtForm((prev) =>
                prev.categoryId || !cats.length ? prev : { ...prev, categoryId: cats[0].id ?? 0 }
            );
        } catch {
            setError('Não foi possível carregar guias.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const handleCatSelect = (item: GuideCategory | null) => {
        setCatForm(item ? { ...item } : { ...emptyCategory });
    };

    const handleArtSelect = (item: GuideArticle | null) => {
        if (item) {
            setArtForm({ ...item });
        } else {
            setArtForm({
                ...emptyArticle,
                categoryId: categories[0]?.id ?? 0,
            });
        }
    };

    const saveCategory = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const payload = {
                slug: catForm.slug.trim() || slugify(catForm.titleEn || catForm.titlePtBr),
                titleEn: catForm.titleEn.trim(),
                titlePtBr: catForm.titlePtBr.trim(),
                sortOrder: Number(catForm.sortOrder) || 0,
                isVisible: catForm.isVisible,
            };
            if (catForm.id) {
                await guideService.updateCategory(catForm.id, { ...payload, id: catForm.id });
            } else {
                await guideService.createCategory(payload);
            }
            await loadAll();
            handleCatSelect(null);
        } catch {
            setError('Erro ao salvar categoria.');
        } finally {
            setSaving(false);
        }
    };

    const saveArticle = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!artForm.categoryId) {
            setError('Selecione uma categoria.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                categoryId: artForm.categoryId,
                slug: artForm.slug.trim() || slugify(artForm.titleEn || artForm.titlePtBr),
                titleEn: artForm.titleEn.trim(),
                titlePtBr: artForm.titlePtBr.trim(),
                summaryEn: artForm.summaryEn.trim(),
                summaryPtBr: artForm.summaryPtBr.trim(),
                contentEn: artForm.contentEn.trim(),
                contentPtBr: artForm.contentPtBr.trim(),
                sortOrder: Number(artForm.sortOrder) || 0,
                isPublished: artForm.isPublished,
            };
            if (artForm.id) {
                await guideService.updateArticle(artForm.id, { ...payload, id: artForm.id });
            } else {
                await guideService.createArticle(payload);
            }
            await loadAll();
            handleArtSelect(null);
        } catch {
            setError('Erro ao salvar artigo.');
        } finally {
            setSaving(false);
        }
    };

    const deleteCategory = async (item: GuideCategory) => {
        if (!item.id) return;
        if (!window.confirm(`Excluir categoria "${item.titlePtBr || item.titleEn}"? Artigos vinculados serão removidos.`))
            return;
        try {
            await guideService.deleteCategory(item.id);
            await loadAll();
            handleCatSelect(null);
        } catch {
            setError('Erro ao excluir categoria.');
        }
    };

    const deleteArticle = async (item: GuideArticle) => {
        if (!item.id) return;
        if (!window.confirm(`Excluir artigo "${item.titlePtBr || item.titleEn}"?`)) return;
        try {
            await guideService.deleteArticle(item.id);
            await loadAll();
            handleArtSelect(null);
        } catch {
            setError('Erro ao excluir artigo.');
        }
    };

    const categoryName = (id: number) => {
        const c = categories.find((x) => x.id === id);
        return c ? c.titlePtBr || c.titleEn : `#${id}`;
    };

    return (
        <div className="admin-guides-page">
            <h1>Guias e tutoriais</h1>
            <p className="admin-subtitle">
                Categorias organizam o menu lateral em /game. Cada artigo é um tutorial com texto em inglês e português.
                Use linhas em branco para parágrafos e <code>- item</code> para listas.
            </p>

            {error && <div className="admin-error">{error}</div>}

            <div className="admin-guides-tabs">
                <button
                    type="button"
                    className={tab === 'articles' ? 'active' : ''}
                    onClick={() => setTab('articles')}
                >
                    Artigos / tutoriais
                </button>
                <button
                    type="button"
                    className={tab === 'categories' ? 'active' : ''}
                    onClick={() => setTab('categories')}
                >
                    Categorias
                </button>
            </div>

            {tab === 'categories' ? (
                <div className="admin-grid">
                    <div className="admin-list">
                        <div className="admin-list-header">
                            <h2>Categorias</h2>
                            <button type="button" className="btn btn-secondary" onClick={() => handleCatSelect(null)}>
                                Nova categoria
                            </button>
                        </div>
                        {loading ? (
                            <p>Carregando...</p>
                        ) : (
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Título PT</th>
                                        <th>Slug</th>
                                        <th>Ordem</th>
                                        <th>Visível</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map((c) => (
                                        <tr
                                            key={c.id}
                                            className={catForm.id === c.id ? 'selected' : ''}
                                            onClick={() => handleCatSelect(c)}
                                        >
                                            <td>{c.titlePtBr || c.titleEn}</td>
                                            <td><code>{c.slug}</code></td>
                                            <td>{c.sortOrder}</td>
                                            <td>{c.isVisible ? 'Sim' : 'Não'}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-small btn-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteCategory(c);
                                                    }}
                                                >
                                                    Excluir
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <form className="admin-form" onSubmit={saveCategory}>
                        <h2>{catForm.id ? 'Editar categoria' : 'Nova categoria'}</h2>
                        <div className="form-row">
                            <label>Slug (URL)</label>
                            <input
                                value={catForm.slug}
                                onChange={(e) => setCatForm({ ...catForm, slug: e.target.value })}
                                placeholder="getting-started"
                            />
                        </div>
                        <div className="form-row-inline">
                            <div className="form-group">
                                <label>Título (EN)</label>
                                <input
                                    value={catForm.titleEn}
                                    onChange={(e) => setCatForm({ ...catForm, titleEn: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Título (PT-BR)</label>
                                <input
                                    value={catForm.titlePtBr}
                                    onChange={(e) => setCatForm({ ...catForm, titlePtBr: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row-inline">
                            <div className="form-group">
                                <label>Ordem</label>
                                <input
                                    type="number"
                                    value={catForm.sortOrder}
                                    onChange={(e) =>
                                        setCatForm({ ...catForm, sortOrder: parseInt(e.target.value, 10) || 0 })
                                    }
                                />
                            </div>
                            <div className="form-row-checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={catForm.isVisible}
                                        onChange={(e) => setCatForm({ ...catForm, isVisible: e.target.checked })}
                                    />
                                    Visível no site
                                </label>
                            </div>
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Salvando...' : 'Salvar categoria'}
                        </button>
                    </form>
                </div>
            ) : (
                <div className="admin-grid">
                    <div className="admin-list">
                        <div className="admin-list-header">
                            <h2>Artigos</h2>
                            <button type="button" className="btn btn-secondary" onClick={() => handleArtSelect(null)}>
                                Novo artigo
                            </button>
                        </div>
                        {loading ? (
                            <p>Carregando...</p>
                        ) : (
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Título PT</th>
                                        <th>Categoria</th>
                                        <th>Publicado</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {articles.map((a) => (
                                        <tr
                                            key={a.id}
                                            className={artForm.id === a.id ? 'selected' : ''}
                                            onClick={() => handleArtSelect(a)}
                                        >
                                            <td>{a.titlePtBr || a.titleEn}</td>
                                            <td>{categoryName(a.categoryId)}</td>
                                            <td>{a.isPublished ? 'Sim' : 'Não'}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-small btn-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteArticle(a);
                                                    }}
                                                >
                                                    Excluir
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <form className="admin-form admin-form--wide" onSubmit={saveArticle}>
                        <h2>{artForm.id ? 'Editar artigo' : 'Novo artigo'}</h2>
                        <div className="form-row-inline">
                            <div className="form-group">
                                <label>Categoria</label>
                                <select
                                    value={artForm.categoryId}
                                    onChange={(e) =>
                                        setArtForm({ ...artForm, categoryId: parseInt(e.target.value, 10) })
                                    }
                                    required
                                >
                                    <option value={0}>Selecione...</option>
                                    {categories.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.titlePtBr || c.titleEn}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Slug (URL /game/...)</label>
                                <input
                                    value={artForm.slug}
                                    onChange={(e) => setArtForm({ ...artForm, slug: e.target.value })}
                                    placeholder="about-empire-icarus"
                                />
                            </div>
                        </div>
                        <div className="form-row-inline">
                            <div className="form-group">
                                <label>Título (EN)</label>
                                <input
                                    value={artForm.titleEn}
                                    onChange={(e) => setArtForm({ ...artForm, titleEn: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Título (PT-BR)</label>
                                <input
                                    value={artForm.titlePtBr}
                                    onChange={(e) => setArtForm({ ...artForm, titlePtBr: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row-inline">
                            <div className="form-group">
                                <label>Resumo (EN)</label>
                                <input
                                    value={artForm.summaryEn}
                                    onChange={(e) => setArtForm({ ...artForm, summaryEn: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Resumo (PT-BR)</label>
                                <input
                                    value={artForm.summaryPtBr}
                                    onChange={(e) => setArtForm({ ...artForm, summaryPtBr: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <label>Conteúdo (EN)</label>
                            <RichNewsEditor
                                value={artForm.contentEn}
                                onChange={(html) => setArtForm({ ...artForm, contentEn: html })}
                                placeholder="Write the guide content in English..."
                            />
                        </div>
                        <div className="form-row">
                            <label>Conteúdo (PT-BR)</label>
                            <RichNewsEditor
                                value={artForm.contentPtBr}
                                onChange={(html) => setArtForm({ ...artForm, contentPtBr: html })}
                                placeholder="Escreva o conteúdo do guia em português..."
                            />
                            <p className="form-hint">
                                Use o editor para formatação, links com texto personalizado, imagens, vídeos e YouTube.
                                Também aceita links externos (outros sites).
                            </p>
                        </div>
                        <div className="form-row-inline">
                            <div className="form-group">
                                <label>Ordem</label>
                                <input
                                    type="number"
                                    value={artForm.sortOrder}
                                    onChange={(e) =>
                                        setArtForm({ ...artForm, sortOrder: parseInt(e.target.value, 10) || 0 })
                                    }
                                />
                            </div>
                            <div className="form-row-checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={artForm.isPublished}
                                        onChange={(e) => setArtForm({ ...artForm, isPublished: e.target.checked })}
                                    />
                                    Publicado
                                </label>
                            </div>
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Salvando...' : 'Salvar artigo'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default AdminGuides;
